import { Link } from "react-router-dom";
import { Linkedin, Mail, Phone, Smartphone, MapPin } from "lucide-react";
import { CERTIFICATIONS, PRACTICES, BRAND, PRIMARY_CTA, BRAND_LOGO_WHITE } from "@/lib/data";

const Footer = () => {
  return (
    <footer className="bg-[#000000] text-white" data-testid="site-footer">
      <div className="container-kc py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand block */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <img src={BRAND_LOGO_WHITE} alt="Kassim Consulting" className="h-16 w-auto" />
              <div className="border-l border-white/20 pl-4">
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/60">Washington, DC</div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#C5A880] font-semibold mt-1">Est. 2022</div>
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed max-w-sm font-serif italic">
              &ldquo;{BRAND.tagline}&rdquo;
            </p>
            <p className="text-white/60 text-xs leading-relaxed max-w-sm mt-6">
              Black-owned, mission-driven, Washington, DC. Founded 2022.
            </p>
            <div className="mt-8 flex flex-col gap-3 text-sm text-white/80">
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 hover:text-[#C5A880] transition-colors" data-testid="footer-email">
                <Mail size={14} className="text-[#C5A880]" /> {BRAND.email}
              </a>
              <a href={`tel:+1${BRAND.office.replace(/\D/g, "")}`} className="flex items-center gap-3 hover:text-[#C5A880] transition-colors" data-testid="footer-office">
                <Phone size={14} className="text-[#C5A880]" /> {BRAND.office} <span className="text-white/50 text-xs">Office</span>
              </a>
              <a href={`tel:+1${BRAND.cell.replace(/\D/g, "")}`} className="flex items-center gap-3 hover:text-[#C5A880] transition-colors" data-testid="footer-cell">
                <Smartphone size={14} className="text-[#C5A880]" /> {BRAND.cell} <span className="text-white/50 text-xs">Cell</span>
              </a>
              <div className="flex items-start gap-3">
                <MapPin size={14} className="text-[#C5A880] mt-0.5 shrink-0" />
                <span>{BRAND.address}</span>
              </div>
            </div>
          </div>

          {/* Practices */}
          <div>
            <div className="eyebrow eyebrow--gold mb-5">Practices</div>
            <ul className="space-y-3 text-sm text-white/80">
              {PRACTICES.map((p) => (
                <li key={p.slug}>
                  <Link to={`/${p.slug}`} className="hover:text-[#C5A880] transition-colors" data-testid={`footer-practice-${p.slug}`}>
                    {p.title}
                  </Link>
                </li>
              ))}
              <li className="pt-2">
                <Link to="/projects" className="hover:text-[#C5A880] transition-colors" data-testid="footer-projects">Projects</Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-[#C5A880] transition-colors" data-testid="footer-about">About</Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-[#C5A880] transition-colors" data-testid="footer-contact">Contact</Link>
              </li>
            </ul>
          </div>

          {/* Certifications */}
          <div>
            <div className="eyebrow eyebrow--gold mb-5">Certifications</div>
            <ul className="space-y-2 text-xs text-white/80 leading-relaxed">
              {CERTIFICATIONS.map((c) => (
                <li key={c.code} className="flex items-start gap-2">
                  <span className="text-[#C5A880] mt-[3px] font-semibold tracking-[0.15em]">{c.code}</span>
                  <span className="text-white/70">{c.label.replace(/^(DC |)/, "")}</span>
                </li>
              ))}
            </ul>

            <div className="eyebrow eyebrow--gold mt-8 mb-4">Connect</div>
            <div className="flex gap-3">
              <a href="https://www.linkedin.com" target="_blank" rel="noreferrer" className="w-9 h-9 inline-flex items-center justify-center border border-white/20 hover:border-[#C5A880] hover:text-[#C5A880] transition-colors" data-testid="social-linkedin">
                <Linkedin size={14} />
              </a>
              <a href={`mailto:${BRAND.email}`} className="w-9 h-9 inline-flex items-center justify-center border border-white/20 hover:border-[#C5A880] hover:text-[#C5A880] transition-colors" data-testid="social-email">
                <Mail size={14} />
              </a>
            </div>
          </div>

          {/* CTA column */}
          <div>
            <div className="eyebrow eyebrow--gold mb-5">Contact Us</div>
            <p className="text-sm text-white/70 leading-relaxed mb-6">
              Tell us about your project and we&apos;ll get back to you.
            </p>
            <Link to={PRIMARY_CTA.to} className="btn btn--inverse w-full justify-between" data-testid="footer-cta">
              {PRIMARY_CTA.label} <span className="btn-arrow">→</span>
            </Link>
          </div>
        </div>

        <div className="hairline hairline--inverse my-12" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/50">
          <div>© {BRAND.copyrightYear} Kassim Consulting. All rights reserved.</div>
          <div className="flex gap-8">
            <Link to="/about" className="hover:text-[#C5A880] transition-colors">About</Link>
            <Link to="/projects" className="hover:text-[#C5A880] transition-colors">Projects</Link>
            <Link to="/contact" className="hover:text-[#C5A880] transition-colors">Contact</Link>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-white/10 text-[11px] text-white/40 text-center tracking-[0.05em]" data-testid="footer-credit">
          Website &amp; SEO built and managed by{" "}
          <a
            href="https://webnehtech.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#C5A880] hover:text-white transition-colors font-semibold"
          >
            Webneh Tech
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
